import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const AuthModal = ({ isOpen, onClose, mode: initialMode, onSwitchMode }) => {
  const { login } = useContext(AuthContext);
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      console.log('Login response:', response.data);
      login(response.data.token, response.data.user);
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created successfully!');
      
      // Wait for state to update then close
      await new Promise(resolve => setTimeout(resolve, 200));
      onClose();
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'An error occurred');
      setLoading(false);
    }
  };

  const switchMode = () => {
    const newMode = mode === 'login' ? 'register' : 'login';
    setMode(newMode);
    if (onSwitchMode) {
      onSwitchMode(newMode);
    }
    setFormData({ email: '', password: '', name: '' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="auth-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'login'
              ? 'Enter your credentials to access your account'
              : 'Sign up to start purchasing press release packages'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {mode === 'register' && (
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                data-testid="auth-name-input"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="auth-email-input"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              data-testid="auth-password-input"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={6}
            />
          </div>
          <Button
            data-testid="auth-submit-btn"
            type="submit"
            className="w-full hover:opacity-90"
            style={{ backgroundColor: '#4AC5DB' }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Please wait...
              </>
            ) : (
              mode === 'login' ? 'Login' : 'Sign Up'
            )}
          </Button>
        </form>
        <div className="mt-4 text-center text-sm">
          <span className="text-gray-600">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
          </span>
          <button
            data-testid="auth-switch-mode-btn"
            type="button"
            onClick={switchMode}
            className="ml-2 hover:underline font-medium"
            style={{ color: '#4AC5DB' }}
          >
            {mode === 'login' ? 'Sign up' : 'Login'}
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 text-center text-sm text-gray-600">
          <p>Need help? Contact us at{' '}
            <a 
              href="mailto:support@alignk.com" 
              className="font-medium hover:underline"
              style={{ color: '#4AC5DB' }}
            >
              support@alignk.com
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;